import React, { useContext, ReactElement, FC, ReactNode } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import DragPointerIcon from '@ad-scripts/presentation/components/common/icons/drag-pointer';
import {
  defaultRenderRowActions,
  RowActionContainer,
  RowProps,
  StyledTD,
  StyledTR,
} from 'src/presentation/components/pages/shared/Table';
import { TBody, TD } from '@ad-scripts/presentation/components/common/table';
import { get, has, map } from 'lodash';

type ItemType = {
  answerIds: string[];
  header: string;
  id: string;
};

type ElementPropsType = {
  columnConfig: any;
  rowDataAid?: string;
  dataAid?: string;
  hideActionsColumn?: boolean;
  item: ItemType;
  renderRowActions?: Function;
};

type PropsType = {
  rows: ReactElement<ElementPropsType>[];
  dragEndHandler: Function;
};

/**
 * For the table to work correctly, each column of content must have a fixed width (columns for actions are an exception) !!!
 */

export default function DraggableTable({ rows, dragEndHandler }: PropsType) {
  const onDragEnd = result => {
    if (!result.destination) {
      return;
    }

    // forbid drop if drag item to own position
    if (result.source.index === result.destination.index) {
      return;
    }

    dragEndHandler({
      currentItemId: rows[result.source.index].props.item.id,
      replaceItemId: rows[result.destination.index].props.item.id,
    });
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {provided => (
          <TBody {...provided.droppableProps} ref={provided.innerRef}>
            {rows.map((row: any, idx: number) => (
              <Draggable key={row.key} draggableId={row.key} index={idx}>
                {(provided, snapshot) => <DraggableRow snapshot={snapshot} provided={provided} {...row.props} />}
              </Draggable>
            ))}
            {provided.placeholder}
          </TBody>
        )}
      </Droppable>
    </DragDropContext>
  );
}

type DraggableRowProps = RowProps & {
  dragProps?: {
    [key: string]: any;
  };
  dragPointer?: ReactNode;
  snapshot?;
  provided?: any;
};

const DraggableRow: FC<DraggableRowProps> = ({
  columnConfig,
  rowDataAid,
  dataAid,
  item,
  renderRowActions,
  snapshot,
  provided,
}) => {
  const theme: any = useContext(ThemeContext);

  const DragPointer = ({ id, isDragging }: { id: string; isDragging: boolean }) => (
    <StyledDragTD>
      <StyledPointerIconWrap data-aid={id} isDragging={isDragging} {...provided.dragHandleProps}>
        <DragPointerIcon width="16px" height="16px" />
      </StyledPointerIconWrap>
    </StyledDragTD>
  );

  const cells = map(columnConfig, ({ attr, defaultValue = '', customStyle = null }) => {
    let value = get(item, attr);
    const attrConfig = columnConfig.find(c => c.attr === attr);
    if (!value && attrConfig && attrConfig.defaultValue) {
      value = attrConfig.defaultValue;
    }

    if (has(attrConfig, 'customRenderer')) {
      value = attrConfig.customRenderer(item);
    }

    const tdDataAid = rowDataAid ? `${rowDataAid}-${attr}` : `${dataAid}-row-${item.id}-${attr}`;
    const tdTitle = typeof value === 'string' ? value : defaultValue;

    return (
      <StyledTD title={tdTitle} data-aid={tdDataAid} key={attr} style={customStyle && customStyle(item)}>
        {value || defaultValue}
      </StyledTD>
    );
  });

  const renderedRowActions = renderRowActions ? renderRowActions({ item }) : defaultRenderRowActions({ item });

  const trDataAid = rowDataAid ? rowDataAid : `${dataAid}-row-${item.id}`;

  return (
    <StyledTR
      data-aid={trDataAid}
      key={item.id}
      ref={provided.innerRef}
      {...provided.draggableProps}
      style={generateRowStyling(snapshot.isDragging, provided.draggableProps.style, theme)}
    >
      <DragPointer id={`${trDataAid}-drag-handler`} isDragging={snapshot.isDragging} />
      {cells}
      <TD style={{ width: '1%' }}>
        <RowActionContainer>{renderedRowActions}</RowActionContainer>
      </TD>
    </StyledTR>
  );
};

function generateRowStyling(isDragging, draggableStyle, theme: any) {
  const styles = {
    userSelect: 'none',
    ...draggableStyle,
  };

  if (isDragging) {
    styles.backgroundColor = `${theme.colors.white}`;
    styles.borderLeft = `4px solid ${theme.colors.purple400}`;
    styles.boxShadow = `${theme.boxShadow.default}`;
    styles.display = 'table';
  }

  return styles;
}

export const StyledDragTD = styled(TD)`
  width: 16px;
  padding: ${({ theme }) => `0px ${theme.spacing.large} !important`};
  padding-right: 0px !important;
`;

export const StyledPointerIconWrap = styled.span`
  width: 17px;
  visibility: hidden;
  ${({ isDragging }) => isDragging && 'visibility: visible'};
`;
